import type { RealtimeEvent } from './helpers';

import { Helmet } from '@dr.pogodin/react-helmet';
import { useApiRead } from 'api-read-hook';
import { differenceInMinutes, getUnixTime, parseISO } from 'date-fns';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { css, html } from 'react-strict-dom';

import {
  PageError,
  PageHeading,
  PageLoading,
  PageWrap,
} from '../../../components';
import { useSelectedCompany } from '../../../contexts/ClientDataContext';
import { useInterval } from '../../../hooks';
import BoxChartMinutes from './BoxChartMinutes';
import BoxChartSeconds from './BoxChartSeconds';
import BoxNumOnline from './BoxNumOnline';
import BoxTopActiveScreens from './BoxTopActiveScreens';
import BoxTopScreens from './BoxTopScreens';

export default function RealtimePage() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const selectedCompany = useSelectedCompany();

  const [lastEventTimestamp, setLastEventTimestamp] = React.useState<
    number | null
  >(null);
  const apiPath = `/company-data/${companySlug}/realtime${
    lastEventTimestamp ? `?since=${lastEventTimestamp}` : ''
  }`;

  const api = useApiRead<{ events: RealtimeEvent[] }>(apiPath, {
    staleWhileInvalidated: true,
  });
  useInterval(api.invalidate, 5000);
  const lastEvents = api.data?.events;

  const [allEvents, setAllEvents] = React.useState<RealtimeEvent[]>([]);

  React.useEffect(() => {
    if (!lastEvents || lastEvents.length === 0) return;
    const lastEvent = lastEvents[lastEvents.length - 1];
    setLastEventTimestamp(getUnixTime(parseISO(lastEvent.timestamp)));
    setAllEvents([
      ...allEvents.filter(
        (event) =>
          differenceInMinutes(new Date(), parseISO(event.timestamp)) <= 30
      ),
      ...lastEvents,
    ]);
  }, [api.data]);

  if (api.error) return <PageError {...api} />;

  const inner = api.data ? (
    <html.div style={styles.grid}>
      <html.div style={[styles.cell, styles.size1]}>
        <BoxNumOnline allEvents={allEvents} />
      </html.div>
      <html.div style={[styles.cell, styles.size1]}>
        <BoxChartMinutes allEvents={allEvents} />
      </html.div>
      <html.div style={[styles.cell, styles.size1]}>
        <BoxChartSeconds allEvents={allEvents} />
      </html.div>
      <html.div style={[styles.cell, styles.size3]}>
        <BoxTopActiveScreens allEvents={allEvents} />
      </html.div>
      <html.div style={[styles.cell, styles.size3]}>
        <BoxTopScreens allEvents={allEvents} />
      </html.div>
    </html.div>
  ) : (
    <PageLoading />
  );

  // console.log(api.data);

  return (
    <PageWrap>
      <Helmet>
        <title>Real-Time | {selectedCompany.name}</title>
      </Helmet>
      <PageHeading>Real-Time</PageHeading>

      {inner}
    </PageWrap>
  );
}

const styles = css.create({
  grid: {
    display: 'flex',
    alignItems: 'stretch',
    flexWrap: 'wrap',
    margin: -15,
  },
  cell: {
    boxSizing: 'border-box',
    padding: 15,
  },
  size1: {
    width: {
      default: '33.33%',
      '@media (max-width: 900px)': '100%',
    },
  },
  size2: {
    width: {
      default: '66.66%',
      '@media (max-width: 900px)': '100%',
    },
  },
  size3: {
    width: {
      default: '100%',
      '@media (max-width: 900px)': '100%',
    },
  },
});
